import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { fetchAgents, fetchAiConnections, setAgentModel, deleteCustomAgent } from '../services/api';
import { Button, Card, Select, Loading, ErrorText, errorMessage } from '../components/ui';
import { formatCost, agentRunCost, totalCost } from '@shared/aiCost';
import { useMe } from '../hooks/useMe';

const NO_OVERRIDE = '';

export default function AgentsPage() {
  const navigate = useNavigate();
  // Creating agents, deleting them and repointing one at a different model are
  // all configuration - the API rejects them for anyone but owner/admin, so
  // don't offer them. Running agents and seeing their cost stays open.
  const { isAdmin } = useMe();
  const [agents, setAgents] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [a, c] = await Promise.all([fetchAgents(), fetchAiConnections()]);
      setAgents(a);
      setConnections(c);
    } catch (err) { setError(errorMessage(err)); }
  }, []);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  async function changeModel(agentId, connectionId) {
    setAgents((prev) => prev.map((a) => (a.id === agentId ? { ...a, connectionId } : a)));
    try {
      await setAgentModel(agentId, connectionId);
    } catch (err) { setError(errorMessage(err)); load(); }
  }

  // "Default connection" is a real selectable option, not just the absence of
  // a choice - picking it clears the per-agent override.
  const modelOptions = [
    { id: NO_OVERRIDE, label: 'Default connection', sublabel: 'tenant default model' },
    ...connections.map((c) => ({ id: c.id, label: c.label, sublabel: c.model })),
  ];

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <h1 style={{ margin: 0 }}>Agents</h1>
        {isAdmin && <Button compact onClick={() => navigate('/agents/new')}>+ New agent</Button>}
      </div>

      {/* Estimated spend across every agent, so the number people actually
          care about is not something they have to add up themselves. */}
      {!loading && agents.length > 0 && (
        <div className="cost-total">
          <div>
            <span className="cost-total-label">Estimated AI spend to date</span>
            <span className="cost-total-value">{formatCost(totalCost(agents))}</span>
          </div>
          <Link to="/settings#ai-pricing" className="cost-total-link">Rates</Link>
        </div>
      )}

      <ErrorText>{error}</ErrorText>
      {loading ? <Loading text="Loading agents…" /> : agents.map((agent) => (
        <Card key={agent.id} title={agent.label} badge={agent.isCustom ? (agent.autoRun === false ? 'custom · manual' : 'custom') : undefined} hint={agent.description}>
          <div className="agent-cost">
            <span className="agent-cost-value">{formatCost(agentRunCost(agent))}</span>
            <span className="agent-cost-meta">
              {(agent.metrics?.runs || 0).toLocaleString()} run{agent.metrics?.runs === 1 ? '' : 's'}
              {agent.metrics?.costEstimated ? ' · rate estimated' : ''}
            </span>
          </div>
          {isAdmin
            ? <Select label="AI model" value={agent.connectionId || NO_OVERRIDE} options={modelOptions} onChange={(v) => changeModel(agent.id, v)} />
            : <div className="agent-model-readonly">AI model: {modelOptions.find((o) => o.id === (agent.connectionId || NO_OVERRIDE))?.label || 'Default connection'}</div>}
          {agent.isCustom && isAdmin && (
            <div className="row">
              <Button compact secondary onClick={() => navigate(`/agents/${encodeURIComponent(agent.id)}`, { state: { agent } })}>Edit</Button>
              <Button compact danger onClick={async () => { await deleteCustomAgent(agent.id); load(); }}>Delete</Button>
            </div>
          )}
        </Card>
      ))}
    </>
  );
}
